// WEG-Bereich der Eigentümer (Handoff Eigentümer §7, E7): Hausgeld je Einheit, Zahlweg,
// Sondereigentum/MEA, Rücklage-Zuführung, ETV-Einladungsfrist und WEG-Unterlagen. Reine Funktionen.

import { istAktuell } from './einheitEigentum';
import { istWegUns, type RollenWerte, type Zuordnung } from './rollen';

export interface WegEinheit {
  id: string;
  objektId: string;
  objektName: string;
  bezeichnung: string;
  flaeche: number;
  mea: number | null;
  hausgeld: number;
  ruecklage: number;
  /** Hausgeld wird aus der SEV-Miete verrechnet (WEG+SEV), sonst SEPA */
  sev: boolean;
}

interface EeRoh { einheit_id: string; eigentuemer_id: string; mea: number | string | null; hausgeld_vorschuss: number | string | null; ruecklage_vorschuss: number | string | null; vertrag_ab: string | null; vertrag_bis: string | null }

const zahl = (v: unknown): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : 0;
};
const runde = (n: number) => Math.round(n * 100) / 100;

/** Einheiten in unseren WEGs mit Hausgeld- und Rücklagenvorschuss (laufende Zeilen dieses Eigentümers). */
export function wegEinheiten(eigId: string, zuordnungen: readonly Zuordnung[], einheitEig: readonly EeRoh[], heute: string): WegEinheit[] {
  const zeilen = new Map(einheitEig.filter(r => r.eigentuemer_id === eigId && istAktuell(r, heute)).map(r => [r.einheit_id, r]));
  return zuordnungen.filter(z => istWegUns(z.rolle)).flatMap(z => z.einheiten.map(e => {
    const r = zeilen.get(e.id);
    return {
      id: e.id, objektId: z.objektId, objektName: z.objektName, bezeichnung: e.bezeichnung, flaeche: e.flaeche, mea: e.mea,
      hausgeld: runde(zahl(r?.hausgeld_vorschuss)), ruecklage: runde(zahl(r?.ruecklage_vorschuss)), sev: z.rolle === 'WEG+SEV',
    };
  }));
}

/** Zahlweg des Hausgelds (Prototyp-Text). */
export function zahlweg(w: Pick<RollenWerte, 'direktHg' | 'hgSevUns'>): string {
  if (w.direktHg > 0 && w.hgSevUns > 0) return 'SEPA + Verrechnung';
  if (w.hgSevUns > 0) return 'Verrechnung';
  return 'SEPA-Lastschrift';
}

/** Zusammensetzung: Hausgeldvorschuss (Bewirtschaftung) und Erhaltungsrücklage, monatlich. */
export function zusammensetzung(e: readonly WegEinheit[]): { hausgeld: number; ruecklage: number; gesamt: number } {
  const hausgeld = runde(e.reduce((s, x) => s + x.hausgeld, 0));
  const ruecklage = runde(e.reduce((s, x) => s + x.ruecklage, 0));
  return { hausgeld, ruecklage, gesamt: runde(hausgeld + ruecklage) };
}

/** Stimmen nach Kopfprinzip (§ 25 Abs. 2 WEG): eine je Gemeinschaft, unabhängig von der Zahl der Einheiten. */
export function stimmen(e: readonly WegEinheit[]): number {
  return new Set(e.map(x => x.objektId)).size;
}

export interface RuecklageWerte {
  objektId: string;
  objektName: string;
  /** Σ Rücklagenzuführung aller laufenden Einheiten der WEG je Jahr */
  zufuehrungWeg: number;
  /** Anteil dieses Eigentümers je Jahr */
  zufuehrungEigen: number;
  /** MEA dieses Eigentümers in der WEG */
  mea: number;
}

/** Zuführung zur Erhaltungsrücklage je WEG – aus den Vorschüssen aller Einheiten (Stand der Rücklage: Buchhaltung). */
export function ruecklageWerte(eigene: readonly WegEinheit[], alleZeilen: readonly (EeRoh & { objekt_id: string })[], heute: string): RuecklageWerte[] {
  const objekte = [...new Map(eigene.map(e => [e.objektId, e.objektName])).entries()];
  return objekte.map(([objektId, objektName]) => {
    const laufend = alleZeilen.filter(r => r.objekt_id === objektId && istAktuell(r, heute));
    const eigen = eigene.filter(e => e.objektId === objektId);
    return {
      objektId, objektName,
      zufuehrungWeg: Math.round(laufend.reduce((s, r) => s + zahl(r.ruecklage_vorschuss), 0) * 12),
      zufuehrungEigen: Math.round(eigen.reduce((s, e) => s + e.ruecklage, 0) * 12),
      mea: runde(eigen.reduce((s, e) => s + (e.mea ?? 0), 0)),
    };
  });
}

/** Einladung zur ETV spätestens 3 Wochen vorher (§ 24 Abs. 4 WEG). */
export function einladungBis(termin: string): string {
  const d = new Date(`${termin.slice(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 21);
  return d.toISOString().slice(0, 10);
}

/** ETV-Termin am Titel oder Typ erkennen. */
export const istEtv = (t: { titel: string | null; typ: string | null }): boolean =>
  /\betv\b|eigentümerversammlung|eigentuemerversammlung|wohnungseigentümerversammlung/i.test(`${t.titel ?? ''} ${t.typ ?? ''}`);

export type WegUnterlagenArt = 'einzelabrechnung' | 'wirtschaftsplan' | 'vermoegensbericht' | 'sonderumlage' | 'p35a' | 'protokoll' | 'beschluss' | 'teilungserklaerung' | 'grundbuch';

const MUSTER: readonly [WegUnterlagenArt, RegExp, string][] = [
  ['einzelabrechnung', /einzelabrechnung|jahresabrechnung|hausgeldabrechnung|wohngeldabrechnung/i, 'Abrechnung'],
  ['wirtschaftsplan', /wirtschaftsplan/i, 'Wirtschaftsplan'],
  ['vermoegensbericht', /verm(ö|oe)gensbericht/i, 'Vermögensbericht'],
  ['sonderumlage', /sonderumlage/i, 'Sonderumlage'],
  ['p35a', /35\s?a|haushaltsnah/i, '§ 35a'],
  ['protokoll', /protokoll/i, 'Protokoll'],
  ['beschluss', /beschluss/i, 'Beschluss'],
  ['teilungserklaerung', /teilungserkl(ä|ae)rung/i, 'Teilungserklärung'],
  ['grundbuch', /grundbuch/i, 'Grundbuch'],
];

/** Art einer WEG-Unterlage aus Titel/Dateiname/Kategorie (null = keine WEG-Unterlage). */
export function unterlagenArt(d: { titel: string | null; datei_name: string | null; kategorie: string | null }): { art: WegUnterlagenArt; label: string } | null {
  const text = `${d.titel ?? ''} ${d.datei_name ?? ''} ${d.kategorie ?? ''}`;
  // Übergabe-/Einzugs-/Auszugsprotokolle gehören zum Mietverhältnis, nicht zur WEG
  const mietProtokoll = /(ü|ue)bergabe|einzug|auszug|abnahme|wohnungs(ü|ue)bergabe/i.test(text);
  const t = MUSTER.find(([art, re]) => re.test(text) && !(art === 'protokoll' && mietProtokoll));
  return t ? { art: t[0], label: t[2] } : null;
}

// ── Verwalterbestellung (§ 26 WEG) ─────────────────────────────────────────────

/** Bestellung höchstens 5 Jahre (§ 26 Abs. 2 WEG; Erstbestellung nach Begründung höchstens 3 Jahre). */
export function bestellungFehler(seit: string, bis: string): string | null {
  if (!seit || !bis) return null;
  if (bis < seit) return 'Ende liegt vor dem Beginn';
  const [j, m, t] = seit.split('-').map(Number) as [number, number, number];
  const grenze = new Date(Date.UTC(j + 5, m - 1, t));
  grenze.setUTCDate(grenze.getUTCDate() - 1);
  return bis > grenze.toISOString().slice(0, 10) ? 'Höchstens 5 Jahre (§ 26 Abs. 2 WEG)' : null;
}

/** Hinweis zur Wiederbestellung: abgelaufen bzw. endet in den nächsten 6 Monaten. */
export function bestellungStatus(bis: string | null, heute: string): 'abgelaufen' | 'endet_bald' | null {
  if (!bis) return null;
  if (bis < heute) return 'abgelaufen';
  const [j, m, t] = heute.split('-').map(Number) as [number, number, number];
  const halb = new Date(Date.UTC(j, m - 1 + 6, t)).toISOString().slice(0, 10);
  return bis <= halb ? 'endet_bald' : null;
}
